import { timepointLabel } from '../../api/know'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { admin, type QCatalogEntry, type QEditorItem } from '../../api/admin'
import { ApiError } from '../../api/client'

/**
 * `/admin/questionnaire-editor` — VISOR de cuestionarios, no editor.
 *
 * Estado real (2026-09-08, pedido de mg): la pantalla anterior listaba sólo
 * el nombre y la cantidad de ítems de SMTQ y CSAI-2R, no dejaba ver ni una
 * pregunta, y el chequeo previo (PRE-SESION) no aparecía en ningún lado
 * porque el endpoint salía de `QUESTIONNAIRES` (el catálogo *asignable*).
 * Encima ofrecía crear/editar/versionar cuestionarios propios, que es
 * exactamente lo que no queremos: los instrumentos y sus timepoints los
 * define el whitepaper del protocolo (tutito, workspace VerSports).
 *
 * Ahora: TODOS los cuestionarios, todas sus preguntas (tipo, opciones,
 * escala, orden, subescala, inversión) y en qué momento del flujo se
 * responden. Cero botones de escritura.
 */
export function AdminQuestionnaireViewer() {
  const [items, setItems] = useState<QCatalogEntry[] | null>(null)
  const [countMeans, setCountMeans] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [openCode, setOpenCode] = useState<string | null>(null)

  useEffect(() => {
    admin
      .qCatalog()
      .then((r) => {
        setItems(r.items)
        setCountMeans(r.response_count_means)
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [])

  const open = useMemo(
    () => items?.find((i) => i.code === openCode) ?? null,
    [items, openCode],
  )

  return (
    <section>
      <div className="page-header">
        <h1>Cuestionarios del protocolo</h1>
        <Link to="/admin/questionnaires" className="btn no-underline">
          Ver respuestas →
        </Link>
      </div>
      <p className="text-sm text-text-muted">
        Vista de <strong>solo lectura</strong>. Los cuestionarios, sus preguntas y
        los momentos en que se responden los define el protocolo científico: no se
        crean, editan ni borran desde el backoffice, para que la clave de
        corrección y la comparabilidad entre olas no se muevan.
      </p>

      {error && <div className="banner-err mt-md">{error}</div>}
      {items === null && !error && (
        <p className="text-sm text-text-muted">Cargando…</p>
      )}

      <div className="mt-md flex flex-col gap-md">
        {items?.map((q) => (
          <QuestionnaireCard
            key={q.code}
            q={q}
            countMeans={countMeans}
            open={openCode === q.code}
            onToggle={() => setOpenCode((c) => (c === q.code ? null : q.code))}
          />
        ))}
      </div>

      {open && <p className="sr-only">Abierto: {open.code}</p>}
    </section>
  )
}

const KIND_LABEL: Record<QCatalogEntry['kind'], string> = {
  validado: 'Instrumento validado',
  interno: 'Interno del sistema',
  custom: 'Propio (creado en base)',
}

/**
 * Una tarjeta del visor. Exportada para que el test pueda montarla con datos
 * reales: `AdminQuestionnaireViewer` carga el catálogo en un efecto, y
 * `renderToStaticMarkup` no corre efectos.
 */
export function QuestionnaireCard({
  q,
  countMeans,
  open,
  onToggle,
}: {
  q: QCatalogEntry
  countMeans: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="card border-outline">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-md text-left"
      >
        <div>
          <div className="font-display text-lg font-bold text-primary">
            {q.code}
            {q.archived && <span className="tag ml-2">archivado</span>}
          </div>
          <div className="text-sm">{q.name_es}</div>
          <div className="mt-xs flex flex-wrap gap-xs text-xs">
            <span className="tag normal-case tracking-normal">{KIND_LABEL[q.kind]}</span>
            <span className="tag normal-case tracking-normal">{q.n_items} ítems</span>
            <span className="tag normal-case tracking-normal">solo lectura</span>
            {q.timepoints.length > 0 && (
              <span className="tag normal-case tracking-normal">
                {q.timepoints.map(timepointLabel).join(' · ')}
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-xl font-bold">{q.response_count}</div>
          <div className="text-xs text-text-muted" title={countMeans}>
            respuestas guardadas
          </div>
          <div className="mt-xs text-xs text-primary">{open ? 'Cerrar' : 'Ver preguntas'}</div>
        </div>
      </button>

      {open && (
        <div className="mt-md border-t border-outline pt-md">
          <QuestionnaireBody q={q} countMeans={countMeans} />
        </div>
      )}
    </div>
  )
}

function QuestionnaireBody({ q, countMeans }: { q: QCatalogEntry; countMeans: string }) {
  const d = q.definition
  return (
    <>
      <h3 className="mt-0">Cuándo se responde</h3>
      <p className="text-sm text-text-muted">{q.flow_es}</p>
      <p className="text-xs text-text-muted">
        Olas: {q.timepoints.length > 0 ? q.timepoints.map(timepointLabel).join(', ') : 'no se asigna por ola'} ·
        Definición: {q.source} · {q.response_count} respuestas guardadas ({countMeans})
      </p>

      {d.instructions_es && (
        <>
          <h3>Consigna</h3>
          <p className="text-sm">{d.instructions_es}</p>
        </>
      )}

      {d.likert.length > 0 && (
        <>
          <h3>Escala Likert</h3>
          <p className="text-sm text-text-muted">
            {d.likert
              .map((o) => `${o.value}${o.label ? ` = ${o.label}` : ''}`)
              .join(' · ')}
          </p>
        </>
      )}

      <h3>Preguntas ({d.items.length})</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Orden</th>
              <th>Pregunta</th>
              <th>Tipo</th>
              <th>Opciones / rango</th>
              <th>Subescala</th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((it, i) => (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>
                  {it.text}
                  {!it.required && <span className="tag ml-1">opcional</span>}
                  {it.reverse && <span className="tag ml-1">inverso</span>}
                  {it.help_es && (
                    <div className="text-xs text-text-muted">{it.help_es}</div>
                  )}
                </td>
                <td className="text-xs">{itemKindLabel(it)}</td>
                <td className="text-xs text-text-muted">{itemRangeLabel(it)}</td>
                <td className="text-xs text-text-muted">{it.subscale || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {d.subscales.length > 0 && (
        <>
          <h3>Subescalas</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Subescala</th>
                  <th>Ítems</th>
                  <th>Rango</th>
                  <th>Qué mide</th>
                </tr>
              </thead>
              <tbody>
                {d.subscales.map((s) => (
                  <tr key={s.key}>
                    <td>{s.label}</td>
                    <td className="text-xs text-text-muted">{s.items.join(', ')}</td>
                    <td className="text-xs text-text-muted">
                      {s.min_score}–{s.max_score}
                    </td>
                    <td className="text-xs text-text-muted">{s.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.total_min != null && d.total_max != null && (
            <p className="text-xs text-text-muted">
              Total: {d.total_min}–{d.total_max}
            </p>
          )}
        </>
      )}

      {d.notes_es && (
        <>
          <h3>Notas de corrección</h3>
          <p className="text-sm text-text-muted">{d.notes_es}</p>
        </>
      )}
    </>
  )
}

/** Etiqueta legible del tipo de ítem — el vocabulario es el de `Item.kind`. */
export function itemKindLabel(it: Pick<QEditorItem, 'kind'>): string {
  switch (it.kind) {
    case 'likert':
      return 'Likert'
    case 'single_select':
      return 'Opción única'
    case 'multi_select':
      return 'Opción múltiple'
    case 'number':
      return 'Número'
    case 'slider':
      return 'Deslizador'
    case 'text':
      return 'Texto libre'
    case 'date':
      return 'Fecha'
    default:
      return it.kind
  }
}

/**
 * Qué puede contestar el jugador en ese ítem: las opciones si las hay, el
 * rango si es numérico, el largo máximo si es texto. Es lo que la pantalla
 * vieja no mostraba en ningún lado.
 */
export function itemRangeLabel(it: QEditorItem): string {
  if (it.options.length > 0) {
    return it.options.map((o) => o.label || o.value).join(' · ')
  }
  if (it.min_value != null || it.max_value != null) {
    const unit = it.unit ? ` ${it.unit}` : ''
    return `${it.min_value ?? '—'}–${it.max_value ?? '—'}${unit}`
  }
  if (it.kind === 'text') return `hasta ${it.max_length} caracteres`
  if (it.kind === 'likert') return 'según la escala Likert'
  return '—'
}
